import React, { useCallback, useEffect, useState, useRef } from 'react';
import cx from 'classnames';
import { LinkWrapper } from 'app/src/components/RouterLink/LinkWrapper';
import { BoardSelectorTile } from 'app/src/components/BoardSelector/BoardSelectorTile';

import styles from './NameCell.less';
import { Tooltip } from '@trello/nachos/tooltip';
import { CellWrapper } from './CellWrapper';
import { Analytics } from '@trello/atlassian-analytics';
import { Preview } from 'app/common/lib/util/preview';
import { Button } from '@trello/nachos/button';
import { EditIcon } from '@trello/nachos/icons/edit';
import {
  Popover,
  PopoverPlacement,
  PopoverScreen,
  usePopover,
} from '@trello/nachos/popover';
import { useUpdateCardNameMutation } from './UpdateCardNameMutation.generated';
import { forTemplate } from '@trello/i18n';

const format = forTemplate('multi_board_table_view');

interface NameCellProps {
  id: string;
  name: string;
  url: string;
  idOrg: string;
  idBoard: string;
  boardName: string;
  boardBackgroundImage?: string | null;
  boardBackgroundTile?: boolean | null;
  boardBackgroundColor?: string | null;
  boardBackgroundImageScaled?: Preview[] | null;
  editable?: boolean;
}

enum Screen {
  CurrentRenameScreen,
}

export const NameCell: React.FunctionComponent<NameCellProps> = React.memo(
  ({
    id,
    name,
    url,
    idOrg,
    idBoard,
    boardName,
    boardBackgroundColor,
    boardBackgroundImage,
    boardBackgroundImageScaled,
    boardBackgroundTile,
    editable,
  }) => {
    const {
      toggle,
      hide,
      triggerRef,
      popoverProps,
    } = usePopover<HTMLDivElement>({
      initialScreen: Screen.CurrentRenameScreen,
    });

    const [updateName] = useUpdateCardNameMutation();

    const editTextAreaRef = useRef<HTMLTextAreaElement | null>(null);

    const [fieldText, setFieldText] = useState<string>(name || '');

    useEffect(() => {
      if (popoverProps.isVisible) {
        Analytics.sendScreenEvent({
          name: 'cardNameChangeInlineDialog',
          containers: {
            organization: {
              id: idOrg,
            },
          },
        });
      }

      setTimeout(() => {
        if (editTextAreaRef.current) {
          editTextAreaRef.current.focus();
          editTextAreaRef.current.setSelectionRange(
            0,
            editTextAreaRef.current.textLength,
          );
        }
      }, 0);
    }, [idOrg, popoverProps.isVisible]);

    const onClick = useCallback(
      (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        Analytics.sendClickedButtonEvent({
          source: 'multiBoardTableViewScreen',
          buttonName: 'cardNameButton',
          containers: {
            organization: {
              id: idOrg,
            },
          },
        });
      },
      [idOrg],
    );

    const onClickChangeName = useCallback(() => {
      Analytics.sendClickedButtonEvent({
        source: 'multiBoardTableViewScreen',
        buttonName: 'openCardNameChangeButton',
        containers: {
          organization: {
            id: idOrg,
          },
        },
      });

      toggle();
    }, [toggle, idOrg]);

    const saveNameChange = useCallback(async () => {
      Analytics.sendClickedButtonEvent({
        source: 'cardNameChangeInlineDialog',
        buttonName: 'saveCardNameChangeButton',
        containers: {
          organization: {
            id: idOrg,
          },
        },
      });

      if (!fieldText.includes('\n') && fieldText !== '') {
        const traceId = Analytics.startTask({
          taskName: 'edit-card/name',
          source: 'cardNameChangeInlineDialog',
        });

        try {
          hide();
          await updateName({
            variables: {
              idCard: id,
              name: fieldText,
              traceId,
            },
          });

          Analytics.sendUpdatedCardFieldEvent({
            field: 'name',
            source: 'cardNameChangeInlineDialog',
            containers: {
              board: { id: idBoard },
              card: { id: id },
              organization: { id: idOrg },
            },
            attributes: {
              taskId: traceId,
            },
          });

          Analytics.taskSucceeded({
            taskName: 'edit-card/name',
            traceId,
            source: 'cardNameChangeInlineDialog',
          });
        } catch (error) {
          throw Analytics.taskFailed({
            taskName: 'edit-card/name',
            traceId,
            source: 'cardNameChangeInlineDialog',
            error,
          });
        }
      }
    }, [fieldText, updateName, id, idOrg, idBoard, hide]);

    const cardLink = (
      <LinkWrapper
        target="_blank"
        href={url}
        onClick={onClick}
        className={styles.nameCell}
      >
        <Tooltip content={boardName} hideTooltipOnMouseDown>
          <BoardSelectorTile
            backgroundColor={boardBackgroundColor}
            backgroundImageScaled={boardBackgroundImageScaled}
            boardThumbnailClassName={styles.boardTile}
            backgroundImage={boardBackgroundImage}
            backgroundTile={boardBackgroundTile}
          />
        </Tooltip>
        <span className={styles.cardName}>{name}</span>
      </LinkWrapper>
    );

    const onClickEdit: React.MouseEventHandler<HTMLButtonElement> = useCallback(
      (e) => {
        e.preventDefault();
        onClickChangeName();
      },
      [onClickChangeName],
    );

    const onChangeName: React.ChangeEventHandler<HTMLTextAreaElement> = useCallback(
      (e) => {
        const text = e.currentTarget.value;
        const noNewLines = text.replace(/\n/g, '');
        e.currentTarget.value = noNewLines;
        setFieldText(noNewLines);
      },
      [],
    );

    return (
      <>
        <CellWrapper
          className={cx(styles.nameCellWrapper, {
            [styles.isExpanded]: popoverProps.isVisible,
          })}
          hovered={popoverProps.isVisible}
          clickable
          hideIcon
        >
          {editable && (
            <Tooltip
              content={format('edit-card-title')}
              hideTooltipOnMouseDown
              tag="div"
            >
              <div className={styles.editButtonWrapper} ref={triggerRef}>
                <Button
                  appearance="subtle"
                  className={styles.editButton}
                  onClick={onClickEdit}
                  iconBefore={
                    <EditIcon
                      size="medium"
                      dangerous_className={styles.editIcon}
                    ></EditIcon>
                  }
                ></Button>
              </div>
            </Tooltip>
          )}
          <Tooltip
            content={format('open-board')}
            hideTooltipOnMouseDown
            tag="span"
          >
            {cardLink}
          </Tooltip>
        </CellWrapper>
        <Popover
          {...popoverProps}
          title={format('change-title')}
          placement={PopoverPlacement.BOTTOM}
        >
          <PopoverScreen id={Screen.CurrentRenameScreen}>
            <div className={styles.popoverContent}>
              <textarea
                className={styles.nameTextArea}
                ref={editTextAreaRef}
                onChange={onChangeName}
                defaultValue={name}
              ></textarea>
              <Button
                appearance="primary"
                className={styles.saveButton}
                isDisabled={fieldText.trim() === ''}
                onClick={saveNameChange}
              >
                {format('save')}
              </Button>
            </div>
          </PopoverScreen>
        </Popover>
      </>
    );
  },
);
